import React, { type FC, useCallback, useMemo } from 'react';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import {
	ForgeAppsRuntimeContainer,
	type ForgeAppRenderer,
} from '@atlassian/jira-forge-action-apps-controller/src/runtime-container.tsx';
import { getAnalyticsAttributesFromExtension } from '@atlassian/jira-forge-ui-analytics/src/common/utils/get-analytics-attributes-from-extension/index.tsx';
import { ViewAnalyticsWrapper } from '@atlassian/jira-forge-issue-navigator-action/src/analytics/ui/index.tsx';
import { AsyncIssueNavigatorAction } from '@atlassian/jira-forge-issue-navigator-action/src/ui/async/AsyncIssueNavigatorAction.tsx';
import type { IssueNavigatorActionExtensionData } from '@atlassian/jira-forge-ui-types/src/common/types/extension-data.tsx';
import type { IssueNavigatorAction } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';

export const ForgeIssueNavigatorActionApp: ForgeAppRenderer<
	IssueNavigatorAction,
	IssueNavigatorActionExtensionData
> = ({ app, setLoaded, localId, handleError: baseHandleError, closeApp }) => {
	// TO DO: add handling for app timeout
	// https://ecosystem-platform.atlassian.net/browse/ARKEN-1786

	// Force render the default error message (instead of the content from the error).
	// By default baseHandleError accepts error message as a parameter
	const handleError = useCallback(() => {
		baseHandleError && baseHandleError();
	}, [baseHandleError]);

	const analyticsAttributes = useMemo(
		() => ({
			...getAnalyticsAttributesFromExtension(app.extension),
			source: 'issue-navigator',
		}),
		[app.extension],
	);

	return (
		<ViewAnalyticsWrapper attributes={analyticsAttributes}>
			<AsyncIssueNavigatorAction
				localId={localId}
				extensionData={app.extensionData}
				extension={app.extension}
				analyticsAttributes={analyticsAttributes}
				onLoad={setLoaded}
				onInitialRender={setLoaded}
				onError={handleError}
				onClose={closeApp}
			/>
		</ViewAnalyticsWrapper>
	);
};

export const IssueNavigatorActionRuntimeContainer: FC = () => (
	<ErrorBoundary packageName="software" id="forge-issue-navigator-action-runtime-container">
		<ForgeAppsRuntimeContainer forgeAppRenderer={ForgeIssueNavigatorActionApp} />
	</ErrorBoundary>
);
